import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  RefreshCw, 
  Printer,
  Sparkles,
  Loader2,
  Play,
  ClipboardCheck,
  BarChart3,
  Repeat
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const PDSA_STEPS = [
  { 
    key: "plan", 
    label: "PLAN", 
    icon: ClipboardCheck,
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    questions: [
      "What change are we testing?",
      "What do we predict will happen?",
      "What data will we collect?"
    ]
  },
  { 
    key: "do", 
    label: "DO", 
    icon: Play,
    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    questions: [
      "Carry out the test",
      "Document observations",
      "Record any problems"
    ]
  },
  { 
    key: "study", 
    label: "STUDY", 
    icon: BarChart3,
    color: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    questions: [
      "What did we learn?",
      "Did results match prediction?",
      "What surprised us?"
    ]
  },
  { 
    key: "act", 
    label: "ACT", 
    icon: Repeat,
    color: "bg-violet-500/20 text-violet-400 border-violet-500/30",
    questions: [
      "Adopt, adapt, or abandon?",
      "What's the next cycle?",
      "How do we spread success?"
    ]
  },
];

export const PDSATemplate = ({ onAIAssist }) => {
  const [cycleNumber, setCycleNumber] = useState(1);
  const [aim, setAim] = useState("");
  const [measure, setMeasure] = useState("");
  const [steps, setSteps] = useState({
    plan: { change: "", prediction: "", data: "" },
    do: { observations: "", problems: "", notes: "" },
    study: { learned: "", matchedPrediction: "", surprises: "" },
    act: { decision: "", nextCycle: "", spread: "" },
  });
  const [aiLoading, setAiLoading] = useState(false);

  const updateStep = (step, field, value) => {
    setSteps(prev => ({
      ...prev,
      [step]: { ...prev[step], [field]: value }
    }));
  };

  const getAISuggestions = async () => {
    if (!aim.trim()) {
      toast.error("Please enter the aim statement first");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/pdsa`, {
        problem: aim.trim(),
        plan: `Change: ${steps.plan.change}\nPrediction: ${steps.plan.prediction}\nData: ${steps.plan.data}`,
        do_notes: `Observations: ${steps.do.observations}\nProblems: ${steps.do.problems}`,
        study_results: `Learned: ${steps.study.learned}\nMatched prediction: ${steps.study.matchedPrediction}\nSurprises: ${steps.study.surprises}`,
        act_decisions: `Decision: ${steps.act.decision}\nNext cycle: ${steps.act.nextCycle}\nSpread: ${steps.act.spread}`
      }, { withCredentials: true });

      if (onAIAssist) {
        onAIAssist(response.data.draft);
      }
      toast.success("AI analysis complete - check the chat");
    } catch (error) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const startNewCycle = () => {
    setCycleNumber(prev => prev + 1);
    setSteps({
      plan: { change: "", prediction: "", data: "" },
      do: { observations: "", problems: "", notes: "" },
      study: { learned: "", matchedPrediction: "", surprises: "" },
      act: { decision: "", nextCycle: "", spread: "" },
    });
    toast.success(`Starting PDSA Cycle #${cycleNumber + 1}`);
  };

  return (
    <Card className="bento-card print:shadow-none print:border-gray-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-400" />
            PDSA Cycle
            <Badge variant="outline" className="ml-2">Cycle #{cycleNumber}</Badge>
          </CardTitle>
          <div className="flex gap-2 no-print">
            <Button variant="outline" size="sm" onClick={startNewCycle}>
              <RefreshCw className="w-4 h-4 mr-1" /> New Cycle
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={getAISuggestions} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              AI Coach
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Aim & Measure */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <Label className="font-medium">AIM Statement</Label>
            <Textarea
              value={aim}
              onChange={(e) => setAim(e.target.value)}
              placeholder="We aim to [improve X] by [Y amount] by [date]..."
              className="mt-2 bg-transparent"
              rows={2}
              data-testid="pdsa-aim"
            />
          </div>
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <Label className="font-medium">Key Measure</Label>
            <Textarea
              value={measure}
              onChange={(e) => setMeasure(e.target.value)}
              placeholder="How will we know a change is an improvement?"
              className="mt-2 bg-transparent"
              rows={2}
              data-testid="pdsa-measure"
            />
          </div>
        </div>

        {/* PDSA Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PDSA_STEPS.map((step) => (
            <div key={step.key} className={`p-4 rounded-lg border ${step.color}`}>
              <div className="flex items-center gap-2 mb-3">
                <step.icon className="w-5 h-5" />
                <span className="font-bold text-lg">{step.label}</span>
              </div>
              <div className="space-y-3">
                {step.key === "plan" && (
                  <>
                    <div>
                      <Label className="text-xs opacity-70">Change to test</Label>
                      <Input
                        value={steps.plan.change}
                        onChange={(e) => updateStep("plan", "change", e.target.value)}
                        placeholder="What specific change?"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-plan-change"
                      />
                    </div>
                    <div>
                      <Label className="text-xs opacity-70">Prediction</Label>
                      <Input
                        value={steps.plan.prediction}
                        onChange={(e) => updateStep("plan", "prediction", e.target.value)}
                        placeholder="What do you expect to happen?"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-plan-prediction"
                      />
                    </div>
                    <div>
                      <Label className="text-xs opacity-70">Data to collect</Label>
                      <Input
                        value={steps.plan.data}
                        onChange={(e) => updateStep("plan", "data", e.target.value)}
                        placeholder="How will you measure?"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-plan-data"
                      />
                    </div>
                  </>
                )}
                {step.key === "do" && (
                  <>
                    <div>
                      <Label className="text-xs opacity-70">Observations</Label>
                      <Textarea
                        value={steps.do.observations}
                        onChange={(e) => updateStep("do", "observations", e.target.value)}
                        placeholder="What did you observe during the test?"
                        className="mt-1 bg-transparent text-sm"
                        rows={2}
                        data-testid="pdsa-do-observations"
                      />
                    </div>
                    <div>
                      <Label className="text-xs opacity-70">Problems encountered</Label>
                      <Input
                        value={steps.do.problems}
                        onChange={(e) => updateStep("do", "problems", e.target.value)}
                        placeholder="Any issues or barriers?"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-do-problems"
                      />
                    </div>
                  </>
                )}
                {step.key === "study" && (
                  <>
                    <div>
                      <Label className="text-xs opacity-70">What we learned</Label>
                      <Textarea
                        value={steps.study.learned}
                        onChange={(e) => updateStep("study", "learned", e.target.value)}
                        placeholder="Key learnings from the data..."
                        className="mt-1 bg-transparent text-sm"
                        rows={2}
                        data-testid="pdsa-study-learned"
                      />
                    </div>
                    <div>
                      <Label className="text-xs opacity-70">Did it match prediction?</Label>
                      <Input
                        value={steps.study.matchedPrediction}
                        onChange={(e) => updateStep("study", "matchedPrediction", e.target.value)}
                        placeholder="Yes/No and why"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-study-matched"
                      />
                    </div>
                  </>
                )}
                {step.key === "act" && (
                  <>
                    <div>
                      <Label className="text-xs opacity-70">Decision: Adopt, Adapt, or Abandon?</Label>
                      <Input
                        value={steps.act.decision}
                        onChange={(e) => updateStep("act", "decision", e.target.value)}
                        placeholder="What's the decision?"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-act-decision"
                      />
                    </div>
                    <div>
                      <Label className="text-xs opacity-70">Next cycle focus</Label>
                      <Input
                        value={steps.act.nextCycle}
                        onChange={(e) => updateStep("act", "nextCycle", e.target.value)}
                        placeholder="What to test next?"
                        className="mt-1 bg-transparent text-sm"
                        data-testid="pdsa-act-next"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PDSATemplate;
